import * as Sentry from "@sentry/react";

/**
 * Guard against the "NotFoundError: The object can not be found here." crash
 * (and its Chrome wording, "Failed to execute 'removeChild' on 'Node'") that
 * browser translation causes.
 *
 * When Safari's or Chrome's built-in translate rewrites the page, it wraps text
 * nodes in <font> elements and reparents them. React still holds references to
 * the original nodes, so the next time it unmounts or reorders a subtree,
 * removeChild / insertBefore throw because the node is no longer a child of the
 * parent React recorded. Seen in production on ONLINE-ORDERING-R, -D and -F:
 * iOS and Android customers on `de-DE` devices viewing our `/en` pages, where
 * the crash fires while React tears down the checkout tree during navigation.
 *
 * Unlike the booking widget, this app deliberately does NOT set `translate="no"`.
 * We only route `en` and `nl`, so customers on other languages rely on browser
 * translation to read the menu. That makes this guard the primary mitigation
 * here rather than defense in depth: it makes the two DOM calls no-op when the
 * node's parent doesn't match, which is exactly the case that would otherwise
 * throw. Normal React operation is unaffected.
 *
 * IMPORTANT: only safe because online-ordering renders on its own domain. Never
 * run this from a bundle embedded in a third-party page, which would patch the
 * host site's Node.prototype.
 *
 * See facebook/react#11538 for the canonical write-up of this workaround.
 */
export function installTranslationCrashGuard() {
  if (typeof Node !== "function" || !Node.prototype) {
    return;
  }

  // Translation can trigger hundreds of mismatches per page. Report the first
  // one so we can confirm the guard is doing something in production, then stay
  // quiet instead of flooding the console and the breadcrumb ring buffer.
  let reported = false;

  function reportOnce(operation: string, node: Node) {
    if (reported) return;
    reported = true;

    console.warn(
      `[vrodex] Skipped ${operation} on a node reparented by browser translation.`,
      node,
    );
    Sentry.addBreadcrumb({
      category: "translation-crash-guard",
      level: "warning",
      message: `Skipped ${operation} on a reparented node`,
    });
  }

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      reportOnce("removeChild", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportOnce("insertBefore", referenceNode);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
